/**
 * The upload engine behind the drop page (F25).
 *
 * This is the only place in cloud-web that PUTs bytes anywhere. It talks to
 * two different things and the distinction matters:
 *   - cloud-api's `/api/v1/u/:token/*` routes, for planning and completing:
 *     small JSON calls;
 *   - R2 itself, directly, for the bytes, via presigned URLs the plan handed back.
 *     Those never go through `apiFetch`: they carry no session, must not get an
 *     Authorization header (it would break the signature), and return no JSON.
 *
 * WHAT THE PERSON USING THIS UNDERSTANDS: nothing. They are a photographer who
 * was sent a link. So the engine is built so that the naive thing works: drag
 * the folder in, and if anything goes wrong, drag the same folder in again.
 * That is the whole recovery story, and it is why `plan` is idempotent
 * server-side: re-planning returns "done" for what landed and, for a
 * half-finished video, URLs for only the parts R2 is still missing.
 *
 * ETAGS ARE OPTIONAL ON PURPOSE. A multipart PUT returns the part's ETag in a
 * response header, which a browser can only read if the bucket's CORS policy
 * exposes it. If it doesn't, we send no parts at completion and the server
 * reconstructs the manifest with ListParts instead. Slower, always correct,
 * and it means a misconfigured CORS policy degrades rather than breaks.
 */

export interface DropLimits {
  max_bytes: number;
  remaining_bytes: number;
  max_files: number;
  remaining_files: number;
  max_file_bytes: number;
  allowed_types: string[];
}

export interface DropInfo {
  label: string;
  status: string;
  expires_at: string;
  limits: DropLimits;
  uploaded: { files: number; bytes: number };
  protocol: {
    batch_max: number;
    multipart_threshold: number;
    part_size: number;
    resumable: boolean;
  };
  challenge: { provider: string | null; site_key: string | null };
}

type PlannedEntry =
  | { mode: 'done'; client_path: string; key: string; bytes: number }
  | { mode: 'single'; client_path: string; key: string; url: string; bytes: number }
  | {
      mode: 'multipart';
      client_path: string;
      key: string;
      upload_id: string;
      part_size: number;
      part_count: number;
      parts: { part_number: number; url: string }[];
      completed_parts: number[];
      bytes: number;
    }
  | { mode: 'refused'; client_path: string; reason: string; code: string };

export type ItemState = 'waiting' | 'uploading' | 'done' | 'skipped' | 'refused' | 'failed';

export interface UploadItem {
  path: string;
  file: File;
  bytes: number;
  sent: number;
  state: ItemState;
  /** Set for `refused` and `failed`, and shown to the uploader verbatim. */
  message?: string;
}

export interface UploadProgress {
  items: UploadItem[];
  totalBytes: number;
  sentBytes: number;
  done: number;
  failed: number;
  refused: number;
  skipped: number;
  running: boolean;
}

/** Simultaneous transfers. Six is the browser's own per-host ceiling; more just queues. */
const CONCURRENCY = 6;
const RETRIES = 3;

class DropApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
  }
}

/** The path the uploader's own folder structure implies, so re-dropping matches. */
export function pathOf(file: File): string {
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return relative && relative.length > 0 ? relative : file.name;
}

export class DropUploader {
  private items: UploadItem[] = [];
  private cancelled = false;
  /** Returns a fresh Turnstile token for each `plan` call, or null when the link needs none. */
  private challengeToken: () => Promise<string | null> = async () => null;

  constructor(
    private readonly apiBase: string,
    private readonly token: string,
    private readonly onProgress: (progress: UploadProgress) => void,
  ) {}

  /** A Turnstile token works once, and every batch calls `plan`, so this is a getter, not a value. */
  setChallengeTokenSource(source: () => Promise<string | null>) {
    this.challengeToken = source;
  }

  cancel() {
    this.cancelled = true;
  }

  /** Build the progress snapshot, hand it to the caller, and return it. */
  private emit(running: boolean): UploadProgress {
    const progress: UploadProgress = {
      items: [...this.items],
      totalBytes: this.items.reduce((sum, i) => sum + i.bytes, 0),
      sentBytes: this.items.reduce((sum, i) => sum + i.sent, 0),
      done: this.items.filter((i) => i.state === 'done').length,
      failed: this.items.filter((i) => i.state === 'failed').length,
      refused: this.items.filter((i) => i.state === 'refused').length,
      skipped: this.items.filter((i) => i.state === 'skipped').length,
      running,
    };
    this.onProgress(progress);
    return progress;
  }

  private async call<T>(path: string, body: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${this.apiBase}/api/v1/u/${this.token}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      throw new DropApiError(
        'Lost the connection. Check your internet, then send the same files again. Nothing you already sent is lost.',
        'NETWORK',
      );
    }
    const json = (await res.json()) as
      | T
      | { error: { code: string; message: string } };
    if (!res.ok) {
      const envelope = json as { error?: { code: string; message: string } };
      throw new DropApiError(
        envelope.error?.message ?? 'Something went wrong.',
        envelope.error?.code ?? 'UNKNOWN',
      );
    }
    return json as T;
  }

  /**
   * Upload everything. Safe to call again with the same files after a failure
   * or a closed tab. That is exactly how resume is meant to be triggered.
   */
  async upload(files: File[], batchMax: number): Promise<UploadProgress> {
    this.cancelled = false;
    this.items = files.map((file) => ({
      path: pathOf(file),
      file,
      bytes: file.size,
      sent: 0,
      state: 'waiting' as ItemState,
    }));
    this.emit(true);

    for (let offset = 0; offset < this.items.length; offset += batchMax) {
      if (this.cancelled) break;
      const batch = this.items.slice(offset, offset + batchMax);
      let planned: PlannedEntry[];
      try {
        const challengeToken = await this.challengeToken();
        const response = await this.call<{ files: PlannedEntry[] }>('/plan', {
          challenge_token: challengeToken,
          files: batch.map((item) => ({
            path: item.path,
            size: item.bytes,
            content_type: item.file.type || undefined,
          })),
        });
        planned = response.files;
      } catch (err) {
        // A whole batch failing to plan is a link-level problem (expired,
        // revoked, full), not a per-file one, so mark them and stop.
        const message = err instanceof Error ? err.message : 'Could not start the upload.';
        for (const item of batch) {
          item.state = 'failed';
          item.message = message;
        }
        this.emit(false);
        throw err;
      }

      const byPath = new Map(batch.map((item) => [item.path, item]));
      const queue = planned.filter((entry) => {
        const item = byPath.get(entry.client_path);
        if (!item) return false;
        if (entry.mode === 'done') {
          item.state = 'skipped';
          item.sent = item.bytes;
          return false;
        }
        if (entry.mode === 'refused') {
          item.state = 'refused';
          item.message = entry.reason;
          return false;
        }
        return true;
      });
      this.emit(true);

      await this.runPool(queue, byPath);
    }

    return this.emit(false);
  }

  /** Fixed-size worker pool over the planned entries. */
  private async runPool(queue: PlannedEntry[], byPath: Map<string, UploadItem>) {
    let cursor = 0;
    const worker = async () => {
      for (;;) {
        if (this.cancelled) return;
        const index = cursor++;
        const entry = queue[index];
        if (!entry) return;
        const item = byPath.get(entry.client_path);
        if (!item) continue;
        item.state = 'uploading';
        this.emit(true);
        try {
          if (entry.mode === 'single') await this.uploadSingle(entry, item);
          else if (entry.mode === 'multipart') await this.uploadMultipart(entry, item);
          item.state = 'done';
          item.sent = item.bytes;
        } catch (err) {
          item.state = 'failed';
          item.message =
            err instanceof Error ? err.message : 'This file did not finish uploading.';
        }
        this.emit(true);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
  }

  private async uploadSingle(
    entry: Extract<PlannedEntry, { mode: 'single' }>,
    item: UploadItem,
  ) {
    await withRetries(() => putBytes(entry.url, item.file, item.file.type));
    item.sent = item.bytes;
    await this.call('/complete', { path: item.path });
  }

  private async uploadMultipart(
    entry: Extract<PlannedEntry, { mode: 'multipart' }>,
    item: UploadItem,
  ) {
    const done = new Set(entry.completed_parts);
    // Parts already on the server still count towards what the person sees as
    // uploaded, or a resumed 12GB file would show 0% and look like it restarted.
    item.sent = done.size * entry.part_size;
    this.emit(true);

    const collected: { part_number: number; etag: string }[] = [];
    let pending = entry.parts;

    while (pending.length > 0) {
      for (const part of pending) {
        if (this.cancelled) throw new Error('Upload stopped.');
        const start = (part.part_number - 1) * entry.part_size;
        const chunk = item.file.slice(start, Math.min(start + entry.part_size, item.bytes));
        const etag = await withRetries(() => putBytes(part.url, chunk));
        if (etag) collected.push({ part_number: part.part_number, etag });
        done.add(part.part_number);
        item.sent = Math.min(item.bytes, item.sent + chunk.size);
        this.emit(true);
      }
      if (done.size >= entry.part_count) break;
      const highest = Math.max(...done);
      const next = await this.call<{ parts: { part_number: number; url: string }[] }>('/parts', {
        path: item.path,
        after_part: highest,
      });
      pending = next.parts;
      if (pending.length === 0) break;
    }

    await this.call('/complete', {
      path: item.path,
      // Omitted when CORS hid the ETags. The server falls back to ListParts.
      parts: collected.length > 0 ? collected : undefined,
    });
  }
}

/** PUT bytes straight at R2. Returns the ETag when the CORS policy exposes it. */
async function putBytes(url: string, body: Blob, contentType?: string): Promise<string | null> {
  const res = await fetch(url, {
    method: 'PUT',
    body,
    headers: contentType ? { 'content-type': contentType } : undefined,
  });
  if (!res.ok) {
    throw new Error(`Storage refused this file (${res.status}).`);
  }
  return res.headers.get('etag');
}

async function withRetries<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < RETRIES; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      // Linear backoff: a flaky connection is the common case here, and a
      // photographer on hotel wifi is better served by trying again soon.
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Upload failed.');
}

/**
 * Fetch a link's public description. Unauthenticated by design.
 *
 * A network failure here surfaces as `TypeError: Failed to fetch`, which is
 * the exact register this page exists to avoid: the person reading it is a
 * photographer, not a developer, and "Failed to fetch" tells them nothing they
 * can act on. Every failure leaves here as a sentence.
 */
export async function fetchDropInfo(apiBase: string, token: string): Promise<DropInfo> {
  let res: Response;
  try {
    res = await fetch(`${apiBase}/api/v1/u/${token}`);
  } catch {
    throw new DropApiError(
      'We could not reach the upload service. Check your internet connection and reload this page.',
      'NETWORK',
    );
  }
  const json = (await res.json()) as DropInfo | { error: { code: string; message: string } };
  if (!res.ok) {
    const envelope = json as { error?: { code: string; message: string } };
    throw new DropApiError(
      envelope.error?.message ?? 'This upload link is not valid.',
      envelope.error?.code ?? 'UNKNOWN',
    );
  }
  return json as DropInfo;
}
